(() => {
  const rootSelector = '[data-cg-social-reviews]';

  if (window.CGSocialReviewCarousel) {
    window.CGSocialReviewCarousel.init(document);
    return;
  }

  const initializedRoots = new WeakSet();

  const initCarousel = (root) => {
    if (!root || initializedRoots.has(root)) {
      return;
    }

    const viewport = root.querySelector('[data-cg-social-review-viewport]');
    const track = root.querySelector('[data-cg-social-review-track]');
    const slides = Array.from(root.querySelectorAll('[data-cg-social-review-slide]'));
    const previousButton = root.querySelector('[data-cg-social-review-previous]');
    const nextButton = root.querySelector('[data-cg-social-review-next]');
    const dots = Array.from(root.querySelectorAll('[data-cg-social-review-dot]'));
    const status = root.querySelector('[data-cg-social-review-status]');
    const actions = root.querySelector('[data-cg-social-review-actions]');
    const toggleButton = root.querySelector('[data-cg-social-review-toggle]');
    const toggleLabel = root.querySelector('[data-cg-social-review-toggle-label]');

    if (!viewport || !track || !slides.length) {
      return;
    }

    initializedRoots.add(root);

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let autoplayTimer = null;
    let visibilityObserver = null;
    let isInView = !('IntersectionObserver' in window);
    let isHovered = false;
    let hasFocus = false;
    let touchStart = null;
    let layoutFrame = null;
    const mobileQuery = window.matchMedia('(max-width: 759px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplayEnabled = root.dataset.cgSocialReviewAutoplay === 'true' && !reduceMotion && slides.length > 1;
    const autoplayInterval = Math.max(4000, Number.parseInt(root.dataset.cgSocialReviewInterval, 10) || 7000);

    root.classList.add('is-ready');

    const getActiveQuote = () => {
      const activeSlide = slides[activeIndex];

      if (!activeSlide) {
        return null;
      }

      const preferredSelector = mobileQuery.matches
        ? '.cg-home-v2-social__testimonial-quote--mobile'
        : '.cg-home-v2-social__testimonial-quote--desktop';

      return activeSlide.querySelector(preferredSelector) || activeSlide.querySelector('[data-cg-social-review-quote]');
    };

    const updateMobileLayout = () => {
      window.cancelAnimationFrame(layoutFrame);

      layoutFrame = window.requestAnimationFrame(() => {
        const activeSlide = slides[activeIndex];
        const activeQuote = getActiveQuote();
        const activeRating = activeSlide?.querySelector('[data-cg-social-review-rating]');

        if (!mobileQuery.matches || !activeSlide || !activeQuote || !actions) {
          slides.forEach((slide) => slide.classList.remove('is-expanded'));
          slides.forEach((slide) => {
            slide.querySelector('[data-cg-social-review-rating]')?.style.removeProperty('--cg-review-stars-top');
          });
          root.classList.remove('is-expanded');
          root.style.removeProperty('height');
          actions?.style.removeProperty('--cg-review-actions-top');

          if (toggleButton) {
            toggleButton.hidden = true;
            toggleButton.setAttribute('aria-expanded', 'false');
            toggleButton.removeAttribute('aria-controls');
          }

          return;
        }

        const isLong = activeQuote.dataset.cgSocialReviewLong === 'true';

        if (!isLong) {
          activeSlide.classList.remove('is-expanded');
        }

        const isExpanded = isLong && activeSlide.classList.contains('is-expanded');
        root.classList.toggle('is-expanded', isExpanded);
        root.style.removeProperty('height');

        if (toggleButton) {
          toggleButton.hidden = !isLong;
          toggleButton.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
          toggleButton.setAttribute('aria-controls', activeQuote.id);
        }

        if (toggleLabel) {
          toggleLabel.textContent = isExpanded ? 'See less' : 'See more';
        }

        const rootRect = root.getBoundingClientRect();
        const quoteRect = activeQuote.getBoundingClientRect();
        const quoteBottom = quoteRect.bottom - rootRect.top;
        const baselineQuoteBottom = slides.reduce((largestBottom, slide) => {
          const mobileQuote = slide.querySelector('.cg-home-v2-social__testimonial-quote--mobile');

          if (!mobileQuote) {
            return largestBottom;
          }

          return Math.max(largestBottom, mobileQuote.getBoundingClientRect().bottom - rootRect.top);
        }, quoteBottom);
        let controlBottom = rootRect.top + baselineQuoteBottom;

        if (activeRating) {
          const ratingTop = Math.max(0, baselineQuoteBottom + 8);

          slides.forEach((slide) => {
            slide.querySelector('[data-cg-social-review-rating]')?.style.setProperty('--cg-review-stars-top', `${ratingTop}px`);
          });

          controlBottom = activeRating.getBoundingClientRect().bottom;
        }

        const actionTop = Math.max(0, controlBottom - rootRect.top + 6);
        actions.style.setProperty('--cg-review-actions-top', `${actionTop}px`);

        if (isExpanded) {
          const actionsRect = actions.getBoundingClientRect();
          const requiredHeight = actionsRect.bottom - rootRect.top + 28;

          if (requiredHeight > rootRect.height) {
            root.style.height = `${Math.ceil(requiredHeight)}px`;
          }
        }
      });
    };

    const canAutoplay = () => (
      autoplayEnabled &&
      isInView &&
      !isHovered &&
      !hasFocus &&
      !root.classList.contains('is-expanded') &&
      document.visibilityState === 'visible'
    );

    const stopAutoplay = () => {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    };

    const scheduleAutoplay = () => {
      stopAutoplay();

      if (!canAutoplay()) {
        return;
      }

      autoplayTimer = window.setTimeout(() => {
        showSlide(activeIndex + 1, { announce: false });
      }, autoplayInterval);
    };

    const pauseInactiveVideos = () => {
      slides.forEach((slide, index) => {
        if (index === activeIndex) {
          return;
        }

        slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    };

    function showSlide(nextIndex, options = {}) {
      const { announce = true } = options;
      activeIndex = (nextIndex + slides.length) % slides.length;
      track.style.transform = `translate3d(${-activeIndex * 100}%, 0, 0)`;

      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.remove('is-expanded');
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.toggleAttribute('inert', !isActive);
      });

      root.classList.remove('is-expanded');
      root.style.removeProperty('height');

      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (status) {
        status.textContent = `Review ${activeIndex + 1} of ${slides.length}`;
        status.setAttribute('aria-live', announce ? 'polite' : 'off');
      }

      pauseInactiveVideos();
      updateMobileLayout();
      scheduleAutoplay();
    }

    previousButton?.addEventListener('click', () => showSlide(activeIndex - 1));
    nextButton?.addEventListener('click', () => showSlide(activeIndex + 1));

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => showSlide(index));
    });

    toggleButton?.addEventListener('click', () => {
      if (!mobileQuery.matches) {
        return;
      }

      const activeSlide = slides[activeIndex];
      const activeQuote = getActiveQuote();

      if (!activeSlide || activeQuote?.dataset.cgSocialReviewLong !== 'true') {
        return;
      }

      const willExpand = !activeSlide.classList.contains('is-expanded');
      activeSlide.classList.toggle('is-expanded', willExpand);

      if (willExpand) {
        stopAutoplay();
      }

      updateMobileLayout();

      if (!willExpand) {
        scheduleAutoplay();
      }
    });

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showSlide(activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        showSlide(activeIndex + 1);
      }
    });

    viewport.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) {
        touchStart = null;
        return;
      }

      touchStart = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    }, { passive: true });

    viewport.addEventListener('touchend', (event) => {
      if (!touchStart || event.changedTouches.length !== 1) {
        touchStart = null;
        return;
      }

      const deltaX = event.changedTouches[0].clientX - touchStart.x;
      const deltaY = event.changedTouches[0].clientY - touchStart.y;
      touchStart = null;

      if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.25) {
        return;
      }

      showSlide(activeIndex + (deltaX < 0 ? 1 : -1));
    }, { passive: true });

    root.addEventListener('mouseenter', () => {
      isHovered = true;
      stopAutoplay();
    });

    root.addEventListener('mouseleave', () => {
      isHovered = false;
      scheduleAutoplay();
    });

    root.addEventListener('focusin', () => {
      hasFocus = true;
      stopAutoplay();
    });

    root.addEventListener('focusout', () => {
      window.setTimeout(() => {
        hasFocus = root.contains(document.activeElement);
        scheduleAutoplay();
      }, 0);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleAutoplay();
      } else {
        stopAutoplay();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('resize', updateMobileLayout);
    mobileQuery.addEventListener?.('change', updateMobileLayout);

    document.fonts?.ready.then(updateMobileLayout);

    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(([entry]) => {
        isInView = entry.isIntersecting;

        if (isInView) {
          scheduleAutoplay();
        } else {
          stopAutoplay();
        }
      }, { threshold: 0.2 });

      visibilityObserver.observe(root);
    }

    root.cgSocialReviewShow = (index) => showSlide(index, { announce: false });
    root.cgSocialReviewDestroy = () => {
      stopAutoplay();
      window.cancelAnimationFrame(layoutFrame);
      visibilityObserver?.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('resize', updateMobileLayout);
      mobileQuery.removeEventListener?.('change', updateMobileLayout);
      delete root.cgSocialReviewShow;
      delete root.cgSocialReviewDestroy;
    };

    showSlide(activeIndex, { announce: false });
  };

  const init = (container = document) => {
    if (container.matches?.(rootSelector)) {
      initCarousel(container);
    }

    container.querySelectorAll?.(rootSelector).forEach(initCarousel);
  };

  window.CGSocialReviewCarousel = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(rootSelector).forEach((root) => root.cgSocialReviewDestroy?.());
  });
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-cg-social-review-slide]');
    const root = slide?.closest(rootSelector);
    const index = Number.parseInt(slide?.dataset.cgSocialReviewIndex, 10);

    if (root?.cgSocialReviewShow && Number.isInteger(index)) {
      root.cgSocialReviewShow(index);
    }
  });
})();
